import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { DropGranularity, DropUsage, type FiltersDropDateRange } from '@/ApiClient.ts';
import type { DropDataItem } from '@components/edit-poppers/edit-drops/index.vue';
import { dateFormatter } from '@lib/formatters';
import { getDropDateRange } from '@/lib/drop-formatter';
import { useUserConfig } from '@/store/user-config';

export const useDrops = defineStore('drops', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.drop_ListDrops,
    dto: 'DropDto',
    delete: api.drop_DeleteDrop,
    signalR: 'DropsUpdate',
  });

  function getDefaultGranularity(): DropGranularity {
    const configValue = useUserConfig().settings?.drop?.defaultGranularity;
    if (configValue) {
      const granularityMap: Record<string, DropGranularity> = {
        Day: DropGranularity.Day,
        Week: DropGranularity.Week,
        Month: DropGranularity.Month,
        Quarter: DropGranularity.Quarter,
      };
      return granularityMap[configValue] ?? DropGranularity.Day;
    }
    return DropGranularity.Day;
  }

  function byRange(range: FiltersDropDateRange) {
    const fromDate = range.from && new Date(range.from);
    const toDate = range.to && new Date(range.to);

    return globalStoreBase.loaded.value.filter((drop) => {
      const dropDate = new Date(drop.dropDate);

      if (fromDate && fromDate > dropDate) return false;

      if (toDate && toDate < dropDate) return false;
    });
  }

  function byGranularity(granularity: DropGranularity) {
    return globalStoreBase.loaded.value.filter((drop) => drop.granularity === granularity);
  }

  function getDropDataItems(dropIds: number[]): DropDataItem[] {
    return globalStoreBase.getByIds(dropIds).map((drop) => ({
      ...drop,
      usage: DropUsage.All,
    }));
  }

  /**
   * Gets the descriptive tooltip text for a drop based on its granularity.
   * This matches the format shown in drop picker tooltips.
   */
  function getDropDescriptionText(dropDate: string, granularity: DropGranularity): string {
    const date = new Date(dropDate);

    switch (granularity) {
      case DropGranularity.Week: {
        const { start, end } = getDropDateRange(dropDate, granularity);
        return `${dateFormatter(start.toISOString(), 'do MMM')} - ${dateFormatter(end.toISOString(), 'do MMM')}`;
      }

      case DropGranularity.Quarter: {
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        const quarterRanges = {
          1: 'Jan, Feb, Mar',
          2: 'Apr, May, Jun',
          3: 'Jul, Aug, Sep',
          4: 'Oct, Nov, Dec',
        };
        return quarterRanges[quarter as keyof typeof quarterRanges];
      }

      case DropGranularity.Month: {
        return dateFormatter(dropDate, 'MMMM');
      }

      case DropGranularity.Day:
      default: {
        return `${dateFormatter(dropDate, 'EEEE')} (w${dateFormatter(dropDate, 'I')})`;
      }
    }
  }

  const uniqueNames = computed(() =>
    [...new Set(globalStoreBase.loaded.value.map((d) => d.name).filter(Boolean) as string[])].sort((a, b) =>
      a.localeCompare(b)
    )
  );

  return {
    ...globalStoreBase,
    byRange,
    byGranularity,
    getDropDataItems,
    getDropDescriptionText,
    getDefaultGranularity,
    uniqueNames,
  };
});
